/**
 * Banner global de estado de sync (Sprint 4 G).
 *
 * Visible cuando hay algo destacable: offline, sync activo, errores
 * o cola pendiente. En estado feliz (online + nada pendiente + sin
 * errores) no renderiza nada para no contaminar la UI.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { type SyncState, subscribeSyncState, syncNow } from '../offline/sync.js';

export function SyncStatusBanner(): JSX.Element | null {
  const [state, setState] = useState<SyncState | null>(null);

  useEffect(() => subscribeSyncState(setState), []);

  if (!state) return null;

  const totalPending = state.pendingChanges + state.pendingPhotos;
  const happy = state.online && !state.syncing && totalPending === 0 && !state.lastError;
  if (happy) return null;

  let tone: 'offline' | 'syncing' | 'pending' | 'error' = 'pending';
  let title = '';
  let detail = '';
  if (!state.online) {
    tone = 'offline';
    title = 'Sin conexion';
    detail = totalPending > 0 ? `${String(totalPending)} cambios encolados` : 'Trabajo offline';
  } else if (state.syncing) {
    tone = 'syncing';
    title = 'Sincronizando...';
    detail = totalPending > 0 ? `${String(totalPending)} pendientes` : '';
  } else if (state.lastError) {
    tone = 'error';
    title = 'Error de sincronizacion';
    detail = state.lastError;
  } else if (totalPending > 0) {
    tone = 'pending';
    title = `${String(totalPending)} cambios pendientes de sincronizar`;
    detail = state.lastSyncedAt
      ? `Ultima sync: ${new Date(state.lastSyncedAt).toLocaleTimeString('es-DO')}`
      : '';
  }

  const cls = {
    offline: 'bg-slate-800 text-slate-100',
    syncing: 'bg-blue-700 text-white',
    pending: 'bg-amber-500 text-amber-950',
    error: 'bg-red-600 text-white',
  }[tone];

  return (
    <div className={`flex items-center justify-between px-4 py-2 text-xs ${cls}`} role="status">
      <Link to="/sync" className="min-w-0 flex-1 hover:underline">
        <span className="font-semibold">{title}</span>
        {detail && <span className="ml-2 opacity-90">· {detail}</span>}
      </Link>
      {state.online && totalPending > 0 && !state.syncing && (
        <button
          type="button"
          onClick={() => {
            void syncNow();
          }}
          className="ml-3 rounded-md bg-white/20 px-2 py-1 font-medium hover:bg-white/30"
        >
          Reintentar
        </button>
      )}
    </div>
  );
}
